import React, { Fragment } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { SMSSettings } from '../../../../core/api/models';
import ErrorMessage from '../../../../components/text/ErrorMessage';

const UpdateForm: React.FunctionComponent<{
  onSubmit: (data: SMSSettings) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  data?: SMSSettings;
  error?: string;
}> = (props) => {
  const { control, handleSubmit, errors, watch } = useForm<SMSSettings>({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldUnregister: true,
    defaultValues: props.data,
  });

  const enabled = watch('sendSms');
  const otpLength = Number(watch('otpLength'));

  const renderEnabled = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          {/* <label className="font-sm text-uppercase">Franchisee</label> */}
          <Controller
            control={control}
            name="sendSms"
            defaultValue={props.data?.sendSms}
            render={({ onChange, value, name }) => (
              <Fragment>
                <input
                  type="checkbox"
                  id={name}
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
                <label htmlFor={name}>SMS Enabled</label>
              </Fragment>
            )}
          />
          <ErrorMessage>{errors.sendSms?.message}</ErrorMessage>
        </div>
      </div>
    );
  };
  const renderAccountId = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Account ID</label>
          <Controller
            control={control}
            rules={{
              validate: {
                required: (value) =>
                  !enabled || !!value || 'Account ID is required',
              },
            }}
            name="account.accountId"
            defaultValue={props.data?.account?.accountId}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Account ID"
                name={name}
                value={value}
                onChange={onChange}
                id="accountId"
              />
            )}
          />
          <ErrorMessage>{errors.account?.accountId?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderSenderId = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Sender ID</label>
          <Controller
            control={control}
            rules={{
              validate: {
                required: (value) =>
                  !enabled || !!value || 'Sender ID is required',
              },
            }}
            name="account.senderId"
            defaultValue={props.data?.account?.senderId}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Sender ID"
                name={name}
                value={value}
                onChange={onChange}
                id="senderId"
              />
            )}
          />
          <ErrorMessage>{errors.account?.senderId?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderUsername = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Username</label>
          <Controller
            control={control}
            rules={{
              validate: {
                required: (value) =>
                  !enabled || !!value || 'Username is required',
              },
            }}
            name="account.username"
            defaultValue={props.data?.account?.username}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Username"
                name={name}
                value={value}
                onChange={onChange}
                id="username"
              />
            )}
          />
          <ErrorMessage>{errors.account?.username?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderPassword = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Password</label>
          <Controller
            control={control}
            rules={{
              validate: {
                required: (value) =>
                  !enabled || !!value || 'Password is required',
              },
            }}
            name="account.password"
            defaultValue={props.data?.account?.password}
            render={({ onChange, value, name }) => (
              <input
                type="password"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Password"
                name={name}
                value={value}
                defaultValue={value}
                onChange={onChange}
                id="password"
              />
            )}
          />
          <ErrorMessage>{errors.account?.password?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderLiveOtp = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <Controller
            control={control}
            name="liveOtp"
            defaultValue={props.data?.liveOtp}
            render={({ onChange, value, name }) => (
              <Fragment>
                <input
                  type="checkbox"
                  id={name}
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
                <label htmlFor={name}>Live OTP</label>
              </Fragment>
            )}
          />
          <ErrorMessage>{errors.liveOtp?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderDebugOtp = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Debug OTP</label>
          <Controller
            control={control}
            rules={{
              required: 'Debug OTP is required',
              validate: {
                numberOnly: (value) =>
                  new RegExp('\\d+').test(value) || 'Invalid digits in OTP',
                otpLength: (value) =>
                  !otpLength ||
                  value?.length === otpLength ||
                  `OTP should have exact ${otpLength} digits`,
              },
            }}
            name="debugOtp"
            defaultValue={props.data?.debugOtp}
            render={({ onChange, value, name }) => (
              <input
                type="number"
                min={0}
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Debug OTP"
                name={name}
                value={value}
                onChange={onChange}
                id="debugOtp"
              />
            )}
          />
          <ErrorMessage>{errors.debugOtp?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderOtpLength = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">OTP Length</label>
          <Controller
            control={control}
            rules={{
              required: 'OTP Length is required',
              validate: {
                numberOnly: (value) =>
                  new RegExp('\\d+').test(value) || 'Invalid number',
              },
            }}
            name="otpLength"
            defaultValue={props.data?.otpLength}
            render={({ onChange, value, name }) => (
              <input
                type="number"
                min={0}
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Debug OTP"
                name={name}
                value={value}
                onChange={onChange}
                id="otpLength"
              />
            )}
          />
          <ErrorMessage>{errors.otpLength?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderButton = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3 text-right pt-3">
          <button
            type="button"
            className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
            onClick={props.onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
            disabled={props.isSubmitting}
          >
            Update
          </button>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(props.onSubmit)}>
      <div className="p-4">
        <h4 className="mb-3 font-weight-semibold">Update SMS Settings</h4>
        <div className="row gutter-10">
          {renderEnabled()}
          {renderAccountId()}
          {renderSenderId()}
          {renderUsername()}
          {renderPassword()}
          {renderLiveOtp()}
          {renderOtpLength()}
          {renderDebugOtp()}
          <ErrorMessage>{props.error}</ErrorMessage>
          {renderButton()}
        </div>
      </div>
    </form>
  );
};

export default UpdateForm;
